import { z } from 'zod';

/**
 * Contratti di input del canale pubblico del chatbot.
 *
 * Questi tre endpoint sono gli unici del prodotto raggiungibili senza
 * autenticazione da un sito terzo: il widget e' embeddato su domini che non
 * controlliamo, quindi ogni campo qui e' sotto il controllo di un visitatore
 * anonimo.
 *
 * PASSTHROUGH: il widget gira su siti terzi e non possiamo aggiornarlo in
 * sincrono con il backend, quindi i campi extra passano invece di produrre un
 * 400 su una versione piu' recente del widget. Permissivi sui campi EXTRA non
 * significa pero' permissivi sulla TAGLIA e sulla FORMA dei campi noti: senza
 * un tetto esplicito valeva il limite globale del body (50MB) e qualunque
 * stringa era accettata come `role`.
 */

/** Tetto per singolo messaggio del visitatore. Una conversazione di supporto
 *  reale non supera qualche migliaio di caratteri; oltre e' abuso o incidente. */
export const CHATBOT_MESSAGE_MAX_CHARS = 4000;

/** Tetto sui turni di storia rispediti dal client. Il turn handler ne usa
 *  comunque solo gli ultimi 20 (`.slice(-20)`): accettarne di piu' significa
 *  solo pagare parsing e banda per dati che verranno scartati. */
export const CHATBOT_HISTORY_MAX_TURNS = 50;

/**
 * Ruoli accettati nella storia inviata dal client.
 *
 * Il widget filtra gia' `role !== 'system'` prima di spedire, quindi questo
 * enum e' allineato a cio' che il client legittimo manda. Serve a impedire che
 * un chiamante diretto inietti un turno `system` nella lista di messaggi
 * passata al modello, spacciandolo per parte della conversazione.
 */
export const chatbotHistoryRoleSchema = z.enum(['user', 'assistant']);

export const chatbotHistoryTurnSchema = z.object({
  role: chatbotHistoryRoleSchema,
  content: z.string().max(CHATBOT_MESSAGE_MAX_CHARS),
}).passthrough();

export const chatbotMessageBodySchema = z.object({
  message: z.string().min(1, 'Message is required').max(
    CHATBOT_MESSAGE_MAX_CHARS,
    `Il messaggio non puo' superare ${CHATBOT_MESSAGE_MAX_CHARS} caratteri`,
  ),
  session_id: z.string().max(128).optional().nullable(),
  conversation_history: z.array(chatbotHistoryTurnSchema)
    .max(CHATBOT_HISTORY_MAX_TURNS, 'Storia conversazione troppo lunga')
    .optional()
    .nullable(),
  visitor_info: z.object({ page_url: z.string().max(2048).optional().nullable() })
    .passthrough().optional().nullable(),
  stream: z.boolean().optional().nullable(),
}).passthrough();

export const chatbotClickToCallBodySchema = z.object({
  phone: z.string().max(32).refine(
    (p) => p.replace(/\D/g, '').length >= 6,
    'Numero di telefono non valido',
  ),
  name: z.string().max(200).optional().nullable(),
  gdpr_consents: z.array(z.record(z.unknown())).max(20).optional().nullable(),
}).passthrough();

export const chatbotCaptureLeadBodySchema = z.object({
  name: z.string().max(200).optional().nullable(),
  email: z.string().max(320).optional().nullable(),
  phone: z.string().max(32).optional().nullable(),
  session_id: z.string().max(128).optional().nullable(),
  notes: z.string().max(CHATBOT_MESSAGE_MAX_CHARS).optional().nullable(),
  gdpr_consents: z.array(z.record(z.unknown())).max(20).optional().nullable(),
}).passthrough();

export type ChatbotHistoryTurn = z.infer<typeof chatbotHistoryTurnSchema>;
export type ChatbotMessageBody = z.infer<typeof chatbotMessageBodySchema>;
export type ChatbotClickToCallBody = z.infer<typeof chatbotClickToCallBodySchema>;
export type ChatbotCaptureLeadBody = z.infer<typeof chatbotCaptureLeadBodySchema>;

/**
 * Identita' di sessione del canale pubblico.
 *
 * Il `session_id` arriva dal body di un endpoint senza autenticazione, quindi
 * e' interamente scelto dal chiamante, e a valle diventa `contactIdentifier`
 * della memoria semantica. Due conseguenze, entrambe sfruttabili:
 *
 *  - un identificatore che non contiene '@' veniva risolto COME NUMERO DI
 *    TELEFONO contro il CRM del tenant: bastava mandare il numero di un
 *    cliente reale per farne caricare le memorie nel prompt;
 *  - quando il match riusciva, lo scope diventava l'UUID del cliente CRM:
 *    mandare quell'UUID produceva lo stesso effetto senza nemmeno il
 *    matching.
 *
 * Il prefisso rende lo spazio degli id di sessione disgiunto sia dai recapiti
 * sia dagli UUID del CRM, e il canale pubblico passa ora `matchPerson: false`
 * perche' il namespace da solo non basterebbe: `normalizePhoneForComparison`
 * scarta ogni carattere non numerico, quindi anche un id namespaced finirebbe
 * comunque in una query di lookup telefonico.
 *
 * Un id fuori formato non viene rifiutato con 400: se ne conia uno nuovo. Il
 * widget adotta gia' il `session_id` restituito dal server e lo rimanda ai
 * turni successivi, quindi la transizione e' trasparente e nessuna sessione
 * in corso si rompe. Le memorie legate ai vecchi id restano orfane ma ora
 * scadono.
 */
export const CHATBOT_SESSION_PREFIX = 'chatbot-session:';
export const CHATBOT_SESSION_RE = /^chatbot-session:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/**
 * Qui vive solo la VALIDAZIONE del formato, non il conio.
 *
 * Questo package e' bundlato anche per il browser (il widget lo importa
 * tramite il frontend), quindi non puo' dipendere da `node:crypto`. Ed e'
 * corretto anche nel merito: coniare un'identita' di sessione e' una
 * responsabilita' del server, non di un client che gira su un sito terzo.
 * Il conio sta in routes/public/chatbot-public.routes.ts.
 */
/** True se l'id e' stato coniato da noi. Un UUID v4 non e' indovinabile, quindi
 *  il solo controllo di formato impedisce in pratica di imporre la sessione di
 *  un altro visitatore, senza bisogno di un registro lato server. */
export function isMintedChatbotSession(value: unknown): value is string {
  return typeof value === 'string' && CHATBOT_SESSION_RE.test(value);
}


